import React, { useState } from "react";
import {
  Row,
  Column,
  FormGroup,
  TextArea,
  RadioButtonGroup,
  RadioButton,
  TextInput,
} from "carbon-components-react";
import useAuth from "../../hooks/useAuth";
import { useEffect } from "react";

const FinancialPlanForm = ({
  disabled,
  register,
  errors,
  hasBusinessCurrently,
  setHasBusinessCurrently,
  businessPlan,
  getValues,
  isJury,
}) => {
  const [instalations, setInstalations] = useState(0);
  const [equipment, setEquipment] = useState(0);
  const [workingCapital, setWorkingCapital] = useState(0);
  const [otherInvestments, setOtherInvestments] = useState(0);
  const [totalInvestment, setTotalInvestment] = useState(0);
  const [grossProfit, setGrossProfit] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });
  const [indirectCosts, setIndirectCosts] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const [totalProvisions, setTotalProvisions] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const [product1Sales, setProduct1Sales] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });
  const [product2Sales, setProduct2Sales] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const [product3Sales, setProduct3Sales] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const [otherProductSales, setOtherProductSales] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const [totalSales, setTotalSales] = useState({
    2022: 0,
    2023: 0,
    2024: 0,
  });

  const getValue = (businessPlan, entity, field) => {
    return getValues(entity + "." + field)
      ? parseFloat(getValues(entity + "." + field))
      : businessPlan
      ? businessPlan[entity][field]
      : 0;
  };

  const calcInvestmentSubTotals = (businessPlan) => {
    const entity = "investmentPlan";
    setInstalations(
      getValue(businessPlan, entity, "constructionOrPurchase") +
        getValue(businessPlan, entity, "adaptationOrRehabilitation")
    );
    setEquipment(
      getValue(businessPlan, entity, "machineryAndTools") +
        getValue(businessPlan, entity, "furniture") +
        getValue(businessPlan, entity, "vehicles") +
        getValue(businessPlan, entity, "computersAndDevices") +
        getValue(businessPlan, entity, "motorcycle") +
        getValue(businessPlan, entity, "householdAppliances")
    );
    setWorkingCapital(
      getValue(businessPlan, entity, "rawMaterialOrMerchandise") +
        getValue(businessPlan, entity, "licensesAndFees") +
        getValue(businessPlan, entity, "marketing") +
        getValue(businessPlan, entity, "salaries") +
        getValue(businessPlan, entity, "income") +
        getValue(businessPlan, entity, "schoolFees") +
        getValue(businessPlan, entity, "familyRanch") +
        getValue(businessPlan, entity, "leisure")
    );
    setOtherInvestments(
      getValue(businessPlan, entity, "others1") +
        getValue(businessPlan, entity, "others2") +
        getValue(businessPlan, entity, "others3") +
        getValue(businessPlan, entity, "contingencies")
    );
  };

  const calcSales = (businessPlan) => {
    const entity = "salesPlan";
    setProduct1Sales({
      2022:
        getValue(businessPlan, entity, "productService1Price2022") *
        getValue(businessPlan, entity, "productService1Qty2022"),
      2023:
        getValue(businessPlan, entity, "productService1Price2023") *
        getValue(businessPlan, entity, "productService1Qty2023"),
      2024:
        getValue(businessPlan, entity, "productService1Price2024") *
        getValue(businessPlan, entity, "productService1Qty2024"),
    });
    setProduct2Sales({
      2022:
        getValue(businessPlan, entity, "productService2Price2022") *
        getValue(businessPlan, entity, "productService2Qty2022"),
      2023:
        getValue(businessPlan, entity, "productService2Price2023") *
        getValue(businessPlan, entity, "productService2Qty2023"),
      2024:
        getValue(businessPlan, entity, "productService2Price2024") *
        getValue(businessPlan, entity, "productService2Qty2024"),
    });
    setProduct3Sales({
      2022:
        getValue(businessPlan, entity, "productService3Price2022") *
        getValue(businessPlan, entity, "productService3Qty2022"),
      2023:
        getValue(businessPlan, entity, "productService3Price2023") *
        getValue(businessPlan, entity, "productService3Qty2023"),
      2024:
        getValue(businessPlan, entity, "productService3Price2024") *
        getValue(businessPlan, entity, "productService3Qty2024"),
    });
    setOtherProductSales({
      2022:
        getValue(businessPlan, entity, "otherProductServicePrice2022") *
        getValue(businessPlan, entity, "otherProductServiceQty2022"),
      2023:
        getValue(businessPlan, entity, "otherProductServicePrice2023") *
        getValue(businessPlan, entity, "otherProductServiceQty2023"),
      2024:
        getValue(businessPlan, entity, "otherProductServicePrice2024") *
        getValue(businessPlan, entity, "otherProductServiceQty2024"),
    });
  };

  const calcTotalSales = () => {
    setTotalSales({
      2022:
        product1Sales[2022] +
        product2Sales[2022] +
        product3Sales[2022] +
        otherProductSales[2022],
      2023:
        product1Sales[2023] +
        product2Sales[2023] +
        product3Sales[2023] +
        otherProductSales[2023],
      2024:
        product1Sales[2024] +
        product2Sales[2024] +
        product3Sales[2024] +
        otherProductSales[2024],
    });
  };

  const calcGrossProfit = (businessPlan) => {
    const entity = "provisionalPlan";
    setGrossProfit({
      2022:
        getValue(businessPlan, entity, "sales2022") -
        getValue(businessPlan, entity, "goodsAndMaterials2022"),
      2023:
        getValue(businessPlan, entity, "sales2023") -
        getValue(businessPlan, entity, "goodsAndMaterials2023"),
      2024:
        getValue(businessPlan, entity, "sales2024") -
        getValue(businessPlan, entity, "goodsAndMaterials2024"),
    });
  };

  const calcIndirectCosts = (businessPlan) => {
    const entity = "provisionalPlan";
    setIndirectCosts({
      2022:
        getValue(businessPlan, entity, "income2022") +
        getValue(businessPlan, entity, "energy2022") +
        getValue(businessPlan, entity, "water2022") +
        getValue(businessPlan, entity, "internet2022") +
        getValue(businessPlan, entity, "salaries2022") +
        getValue(businessPlan, entity, "equipmentMaintenance2022") +
        getValue(businessPlan, entity, "officeSupplies2022") +
        getValue(businessPlan, entity, "transport2022") +
        getValue(businessPlan, entity, "others2022"),
      2023:
        getValue(businessPlan, entity, "income2023") +
        getValue(businessPlan, entity, "energy2023") +
        getValue(businessPlan, entity, "water2023") +
        getValue(businessPlan, entity, "internet2023") +
        getValue(businessPlan, entity, "salaries2023") +
        getValue(businessPlan, entity, "equipmentMaintenance2023") +
        getValue(businessPlan, entity, "officeSupplies2023") +
        getValue(businessPlan, entity, "transport2023") +
        getValue(businessPlan, entity, "others2023"),
      2024:
        getValue(businessPlan, entity, "income2024") +
        getValue(businessPlan, entity, "energy2024") +
        getValue(businessPlan, entity, "water2024") +
        getValue(businessPlan, entity, "internet2024") +
        getValue(businessPlan, entity, "salaries2024") +
        getValue(businessPlan, entity, "equipmentMaintenance2024") +
        getValue(businessPlan, entity, "officeSupplies2024") +
        getValue(businessPlan, entity, "transport2024") +
        getValue(businessPlan, entity, "others2024"),
    });
  };

  const calcTotalProvisions = () => {
    setTotalProvisions({
      2022: grossProfit[2022] - indirectCosts[2022],
      2023: grossProfit[2023] - indirectCosts[2023],
      2024: grossProfit[2024] - indirectCosts[2024],
    });
  };

  const calcTotalInvestment = () => {
    setTotalInvestment(
      instalations + equipment + workingCapital + otherInvestments
    );
  };

  const handleInvestmentBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcInvestmentSubTotals(businessPlan);
    }
  };

  const handleSalesBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcSales(businessPlan);
    }
  };

  const handleProvisionsBlur = (e) => {
    if (!isNaN(e.target.value)) {
      calcGrossProfit(businessPlan);
      calcIndirectCosts(businessPlan);
    }
  };

  useEffect(() => {
    if (businessPlan) {
      if (businessPlan.investmentPlan) {
        calcInvestmentSubTotals(businessPlan);
      }
      if (businessPlan.salesPlan) {
        calcSales(businessPlan);
      }
      if (businessPlan.provisionalPlan) {
        calcGrossProfit(businessPlan);
        calcIndirectCosts(businessPlan);
      }
    }
    calcTotalInvestment();
    calcTotalSales();
    calcTotalProvisions();
  }, [businessPlan]);

  useEffect(() => {
    calcTotalInvestment();
  }, [instalations, equipment, workingCapital, otherInvestments]);

  useEffect(() => {
    calcTotalSales();
  }, [product1Sales, product2Sales, product3Sales, otherProductSales]);

  useEffect(() => {
    calcTotalProvisions();
  }, [grossProfit, indirectCosts]);

  return (
    <FormGroup legendText="7	PLANO FINANCEIRO " className="section-header">
      <h6 className="section-subtitle">
        A ser preenchida por todos/as candidatos/as independentemente se tem ou
        não um negócio actualmente.
      </h6>
      {!isJury && (
        <fieldset className="bx--fieldset ">
          <legend className="bx--label required">
            7.1 Plano de Investimento com o capital do Agora Emprega
          </legend>
          <div className="bx--form__helper-text">
            [Descreva os itens que vão constituir o plano de investimento a ser
            coberto somente com o valor do financiamento a ser adquirido através
            da CPN, indicando o valor em MZN.{" "}
            <strong>
              Por favor, considere valores reais de acordo com a sua pesquisa de
              mercado
            </strong>
            ]
          </div>
          <table className="table table-bordered ">
            <thead className="thead-light">
              <tr>
                <th scope="col">Descrição do Item (preenche os aplicáveis)</th>
                <th scope="col" className="w-25">
                  Montante (MZN)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>Instalações</strong>
                </td>
                <td className="text-right">
                  <strong>{instalations}</strong>
                </td>
              </tr>
              <tr>
                <td>Construção / Compra</td>
                <td>
                  <TextInput
                    labelText=""
                    id="constructionOrPurchase"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.constructionOrPurchase")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.constructionOrPurchase?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Adaptação ou reabilitação </td>
                <td>
                  <TextInput
                    labelText=""
                    id="adaptationOrRehabilitation"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.adaptationOrRehabilitation")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.adaptationOrRehabilitation?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Equipamento</strong>
                </td>
                <td className="text-right">
                  <strong>{equipment}</strong>
                </td>
              </tr>
              <tr>
                <td>Maquinaria e ferramentas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="machineryAndTools"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.machineryAndTools")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.machineryAndTools?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Mobiliário</td>
                <td>
                  <TextInput
                    labelText=""
                    id="furniture"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.furniture")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.furniture?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Viaturas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="vehicles"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.vehicles")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.vehicles?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Computadores e outros dispositivos </td>
                <td>
                  <TextInput
                    labelText=""
                    id="computersAndDevices"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.computersAndDevices")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.computersAndDevices?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Motorizada</td>
                <td>
                  <TextInput
                    labelText=""
                    id="motorcycle"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.motorcycle")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.motorcycle?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Electrodomésticos</td>
                <td>
                  <TextInput
                    labelText=""
                    id="householdAppliances"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.householdAppliances")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.householdAppliances?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Capital Giro</strong>
                </td>
                <td className="text-right">
                  <strong>{workingCapital}</strong>
                </td>
              </tr>
              <tr>
                <td>Aquisição de matéria prima ou mercadoria</td>
                <td>
                  <TextInput
                    labelText=""
                    id="rawMaterialOrMerchandise"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.rawMaterialOrMerchandise")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.rawMaterialOrMerchandise?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Pagamento de licenças e taxas</td>
                <td>
                  <TextInput
                    labelText=""
                    id="licensesAndFees"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.licensesAndFees")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.licensesAndFees?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Despesas de marketing</td>
                <td>
                  <TextInput
                    labelText=""
                    id="marketing"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.marketing")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.marketing?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Salários</td>
                <td>
                  <TextInput
                    labelText=""
                    id="salaries"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.salaries")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.salaries?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Renda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="income"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.income")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.income?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  Propinas escolares dos dependentes (filhos, sobrinhos, etc.)
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="schoolFees"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.schoolFees")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.schoolFees?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Rancho para família</td>
                <td>
                  <TextInput
                    labelText=""
                    id="familyRanch"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.familyRanch")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.familyRanch?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Aquisição de vestuário para lazer</td>
                <td>
                  <TextInput
                    labelText=""
                    id="leisure"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.leisure")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.leisure?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Outros (especificar):</strong>
                </td>
                <td className="text-right">
                  <strong>{otherInvestments}</strong>
                </td>
              </tr>
              <tr>
                <td>
                  <TextInput
                    labelText=""
                    id="others1Desc"
                    maxLength={255}
                    disabled={disabled}
                    {...register("investmentPlan.others1Desc")}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others1Desc?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="others1"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.others1")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others1?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <TextInput
                    labelText=""
                    id="others2Desc"
                    maxLength={255}
                    disabled={disabled}
                    {...register("investmentPlan.others2Desc")}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others2Desc?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="others2"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.others2")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others2?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <TextInput
                    labelText=""
                    id="others3Desc"
                    maxLength={255}
                    disabled={disabled}
                    {...register("investmentPlan.others3Desc")}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others3Desc?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="others3"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.others3")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.others3?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Contingências (máximo de 7% do orçamento total)</td>
                <td>
                  <TextInput
                    labelText=""
                    id="contingencies"
                    maxLength={12}
                    disabled={disabled}
                    {...register("investmentPlan.contingencies")}
                    onBlur={handleInvestmentBlur}
                  />
                  <p className="field-error">
                    {errors.investmentPlan?.contingencies?.message}
                  </p>
                </td>
              </tr>
              <tr className="table-secondary">
                <td>
                  <strong>
                    Total Geral de Investimento coberto pelo valor da CPN:
                  </strong>
                </td>
                <td className="text-right">
                  <strong>{parseFloat(totalInvestment)}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      )}

      {!isJury && (
        <fieldset className="bx--fieldset mb-3">
          <legend className="bx--label required">7.2 Plano de Vendas </legend>
          <div className="bx--form__helper-text">
            [Indique o volume, preço de venda, valor de venda anual até pelo
            menos 3 anos. Dado que uma empresa pode fornecer mais do que um
            produto (ou serviço), o(a) candidato(a) deve indicar a designação
            específica de cada um deles na tabela abaixo.]
          </div>
          <table className="table table-bordered ">
            <thead className="thead-light">
              <tr>
                <th scope="col" rowSpan={2}>
                  Descrição
                </th>
                <th scope="col" rowSpan={2}>
                  Detalhes
                </th>
                <th scope="col" colSpan={3}>
                  Valor (em MZN)
                </th>
              </tr>
              <tr>
                <th scope="col">2023</th>
                <th scope="col">2024</th>
                <th scope="col">2025</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService1Desc"
                    labelText="Produto/Serviço 1"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService1Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Price2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Price2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Price2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService1Qty2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService1Qty2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService1Qty2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2022]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2023]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product1Sales[2024]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService2Desc"
                    labelText="Produto/Serviço 2"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService2Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Price2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Price2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Price2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService2Qty2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService2Qty2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService2Qty2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2022]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2023]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product2Sales[2024]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="productService3Desc"
                    labelText="Produto/Serviço 3"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.productService3Desc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Desc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Price2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Price2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Price2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="productService3Qty2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.productService3Qty2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.productService3Qty2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2022]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2023]}</strong>
                </td>
                <td className="text-right">
                  <strong>{product3Sales[2024]}</strong>
                </td>
              </tr>
              <tr>
                <td rowSpan={3}>
                  <TextArea
                    id="otherProductServiceDesc"
                    labelText="Outro(s) produto(s) / serviço(s)"
                    rows={5}
                    maxLength={255}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceDesc")}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceDesc?.message}
                  </p>
                </td>
                <td>Preço de Venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServicePrice2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServicePrice2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServicePrice2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>Quantidades previstas para a venda</td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2022"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2022")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2022?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2023"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2023")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2023?.message}
                  </p>
                </td>
                <td>
                  <TextInput
                    labelText=""
                    id="otherProductServiceQty2024"
                    maxLength={12}
                    disabled={disabled}
                    {...register("salesPlan.otherProductServiceQty2024")}
                    onBlur={handleSalesBlur}
                  />
                  <p className="field-error">
                    {errors.salesPlan?.otherProductServiceQty2024?.message}
                  </p>
                </td>
              </tr>
              <tr>
                <td>
                  <strong>Volume de vendas (preço*quantidade)</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2022]}</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2023]}</strong>
                </td>
                <td className="text-right">
                  <strong>{otherProductSales[2024]}</strong>
                </td>
              </tr>
              <tr className="table-secondary">
                <td colSpan={2}>
                  <strong>Total de Vendas</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2022]}</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2023]}</strong>
                </td>
                <td className="text-right">
                  <strong>{totalSales[2024]}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </fieldset>
      )}
      <fieldset className="bx--fieldset">
        <legend className="bx--label required">
          7.3 Plano de Exploração Previsional{" "}
        </legend>
        <div className="bx--form__helper-text">
          [Preencha a tabela que se segue, indicando as vendas previsionais,
          resultados brutos, custos e resultados líquidos que o seu plano de
          negócio propõe-se atingir]
        </div>
        <table className="table table-bordered ">
          <thead className="thead-light">
            <tr>
              <th scope="col" rowSpan={2}>
                Descrição
              </th>
              <th scope="col" colSpan={3}>
                Valor (MZN)
              </th>
            </tr>
            <tr>
              <th scope="col">2023</th>
              <th scope="col">2024</th>
              <th scope="col">2025</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Volume de vendas</td>
              <td>
                <TextInput
                  labelText=""
                  id="sales2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.sales2022")}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.sales2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="sales2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.sales2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.sales2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="sales2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.sales2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.sales2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Custos de mercadorias vendidas e matérias consumidas</td>
              <td>
                <TextInput
                  labelText=""
                  id="goodsAndMaterials2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.goodsAndMaterials2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.goodsAndMaterials2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="goodsAndMaterials2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.goodsAndMaterials2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.goodsAndMaterials2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="goodsAndMaterials2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.goodsAndMaterials2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.goodsAndMaterials2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Lucro Bruto</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2022]}</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2023]}</strong>
              </td>
              <td className="text-right">
                <strong>{grossProfit[2024]}</strong>
              </td>
            </tr>
            <tr>
              <td colSpan={4}>
                <strong>Custos Indirectos (preenche os aplicáveis)</strong>
              </td>
            </tr>
            <tr>
              <td>Renda</td>
              <td>
                <TextInput
                  labelText=""
                  id="income2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.income2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.income2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="income2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.income2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.income2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="income2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.income2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.income2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Energia</td>
              <td>
                <TextInput
                  labelText=""
                  id="energy2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.energy2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.energy2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="energy2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.energy2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.energy2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="energy2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.energy2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.energy2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Água</td>
              <td>
                <TextInput
                  labelText=""
                  id="water2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.water2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.water2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="water2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.water2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.water2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="water2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.water2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.water2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Internet</td>
              <td>
                <TextInput
                  labelText=""
                  id="internet2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.internet2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.internet2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="internet2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.internet2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.internet2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="internet2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.internet2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.internet2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Salários</td>
              <td>
                <TextInput
                  labelText=""
                  id="salaries2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salaries2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salaries2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="salaries2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salaries2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salaries2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="salaries2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.salaries2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.salaries2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Manutenção de Equipamentos</td>
              <td>
                <TextInput
                  labelText=""
                  id="equipmentMaintenance2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.equipmentMaintenance2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.equipmentMaintenance2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="equipmentMaintenance2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.equipmentMaintenance2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.equipmentMaintenance2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="equipmentMaintenance2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.equipmentMaintenance2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.equipmentMaintenance2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Consumíveis de Escritório</td>
              <td>
                <TextInput
                  labelText=""
                  id="officeSupplies2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.officeSupplies2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.officeSupplies2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="officeSupplies2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.officeSupplies2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.officeSupplies2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="officeSupplies2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.officeSupplies2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.officeSupplies2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>Transporte</td>
              <td>
                <TextInput
                  labelText=""
                  id="transport2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.transport2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.transport2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="transport2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.transport2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.transport2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="transport2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.transport2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.transport2024?.message}
                </p>
              </td>
            </tr>

            <tr>
              <td>Outros</td>
              <td>
                <TextInput
                  labelText=""
                  id="others2022"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.others2022")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.others2022?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="others2023"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.others2023")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.others2023?.message}
                </p>
              </td>
              <td>
                <TextInput
                  labelText=""
                  id="others2024"
                  maxLength={12}
                  disabled={disabled}
                  {...register("provisionalPlan.others2024")}
                  onBlur={handleProvisionsBlur}
                />
                <p className="field-error">
                  {errors.provisionalPlan?.others2024?.message}
                </p>
              </td>
            </tr>
            <tr>
              <td>
                <strong>Lucro antes de Juros e impostos</strong>
              </td>
              <td className="text-right">
                <strong>{totalProvisions[2022]}</strong>
              </td>
              <td className="text-right">
                <strong>{totalProvisions[2023]}</strong>
              </td>
              <td className="text-right">
                <strong>{totalProvisions[2024]}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </fieldset>
    </FormGroup>
  );
};

FinancialPlanForm.defaultProps = {
  disabled: false,
};
export default FinancialPlanForm;
